import * as BABYLON from '@babylonjs/core'
import { StudioArt, WallSide } from '../types'

/** World-space anchor + facing normal for a wall side, given the room footprint. */
export function wallGeometry(
  side: WallSide,
  u: number,
  y: number,
  room: { width: number; depth: number },
): { position: BABYLON.Vector3; rotationY: number; normal: BABYLON.Vector3 } {
  const { width: W, depth: D } = room
  if (side === -1) {
    return {
      position: new BABYLON.Vector3(-W / 2, y, u),
      rotationY: Math.PI / 2,
      normal: new BABYLON.Vector3(1, 0, 0),
    }
  }
  if (side === 1) {
    return {
      position: new BABYLON.Vector3(W / 2, y, u),
      rotationY: -Math.PI / 2,
      normal: new BABYLON.Vector3(-1, 0, 0),
    }
  }
  return {
    position: new BABYLON.Vector3(u, y, D / 2),
    rotationY: Math.PI,
    normal: new BABYLON.Vector3(0, 0, -1),
  }
}

/** Warm-white spotlight tint for a given 0..1 warmth dial. */
export function warmSpotlightColor(warmth: number): BABYLON.Color3 {
  return new BABYLON.Color3(1, 1 - 0.28 * warmth, 1 - 0.55 * warmth)
}

/** Print height in meters, derived from width + aspect ratio. */
export function printHeight(art: Pick<StudioArt, 'w' | 'ratio'>): number {
  return art.w / art.ratio
}

/**
 * Clamp a wall side's `u` (slide) so the print can't clip through a corner.
 * Pass `artWidth` so the frame's half-extent is inset, not just the center point
 * (center-only clamp left 1.8m prints jammed into Pavilion corners).
 */
export function clampU(
  side: WallSide,
  u: number,
  room: { width: number; depth: number },
  artWidth = 0,
): number {
  const wallHalf = side === 0 ? room.width / 2 : room.depth / 2
  const bound = Math.max(0.2, wallHalf - artWidth / 2 - 0.35)
  return Math.max(-bound, Math.min(bound, u))
}

/** Deterministic short id — no backend round-trip needed for a freshly hung print. */
export function generateLocalId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
}
